import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GitCompareArrows, Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { data, dinheiro, nomeParceiro } from '../../nucleo/formato';
import { AvisoModo, useSessaoFluxo } from './AvisoModo';
import { Comparativo } from './Comparativo';
import { DetalheCotacao } from './DetalheCotacao';
import { FormCotacao } from './FormCotacao';
import type { Cotacao } from './tipos';

/** Aba Cotações (M10.1): propostas dos fornecedores para as requisições, com comparativo e escolha da vencedora. */
export function Cotacoes() {
  const { t } = useTranslation('compras');
  // Fase 2 (W8): no modo simplificado (ou sem Estoque, no recebimento) as ações do fluxo detalhado somem; a leitura fica.
  const s = useSessaoFluxo();
  const [novo, setNovo] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const [comparar, setComparar] = useState<string | null>(null);
  const [soEscolhidas, setSoEscolhidas] = useState('');

  const colunas: Coluna<Cotacao>[] = [
    { chave: 'requisicao_numero', rotulo: t('campos.requisicao'), mono: true, render: (c) => c.requisicao_numero },
    { chave: 'fornecedor_nome', rotulo: t('campos.fornecedor'), principal: true, render: (c) => <span className="truncate font-medium" title={nomeParceiro(c.fornecedor_nome)}>{nomeParceiro(c.fornecedor_nome)}</span> },
    { chave: 'total', rotulo: t('campos.total'), ordenavel: true, alinhar: 'direita', mono: true, render: (c) => dinheiro(c.total_centavos, c.moeda) },
    { chave: 'prazo', rotulo: t('campos.prazoMaximo'), alinhar: 'direita', mono: true, render: (c) => (c.prazo_maximo_dias === null ? '' : t('comparativo.prazoDias', { count: c.prazo_maximo_dias })) },
    { chave: 'condicao', rotulo: t('campos.condicao'), oculta: true, mono: true, render: (c) => (c.condicao_pagamento === 'avista' ? t('pedido.avista') : (c.condicao_pagamento ?? '')) },
    {
      chave: 'validade',
      rotulo: t('campos.validade'),
      mono: true,
      render: (c) => (c.vencida ? <span className="font-medium text-laranja">{data(c.validade)}</span> : c.validade ? data(c.validade) : ''),
    },
    { chave: 'created_at', rotulo: t('campos.criadoEm'), ordenavel: true, oculta: true, mono: true, render: (c) => data(c.created_at) },
    {
      chave: 'estado',
      rotulo: t('campos.estado'),
      render: (c) => (
        <span className="flex flex-wrap items-center gap-1">
          {c.escolhida && <Selo cor="verde">{t('cotacao.escolhida')}</Selo>}
          {c.vencida && <Selo cor="laranja">{t('cotacao.vencida')}</Selo>}
          <BotaoComparar aoClicar={() => setComparar(c.requisicao_id)} rotulo={t('acoes.compararCurto')} />
        </span>
      ),
    },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <AvisoModo />
      <AcaoPrimaria>
        {s.pode('compras.criar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('acoes.novaCotacao')}
          </Botao>
        )}
      </AcaoPrimaria>

      <Tabela<Cotacao>
        tela="compras.cotacoes"
        caminho="/compras/cotacoes"
        params={{ escolhida: soEscolhidas || undefined }}
        filtros={{ escolhida: soEscolhidas }}
        aoAplicarFiltros={(f) => setSoEscolhidas(f.escolhida === 'true' ? 'true' : '')}
        colunas={colunas}
        aoClicar={(c) => setAberto(c.id)}
        filtrosExtra={<Selecao aria-label={t('filtros.escolha')} vazio={t('filtros.todasCotacoes')} opcoes={[{ valor: 'true', rotulo: t('filtros.soEscolhidas') }]} value={soEscolhidas} onChange={(e) => setSoEscolhidas(e.target.value)} />}
        vazio={<Vazio texto={t('vazio.cotacoes')} />}
      />

      <FormCotacao aberto={novo} aoFechar={() => setNovo(false)} aoSalvar={(id) => setAberto(id)} />
      <DetalheCotacao id={aberto} aoFechar={() => setAberto(null)} />
      <Comparativo requisicaoId={comparar} aoFechar={() => setComparar(null)} />
    </div>
  );
}

/** Botão pequeno dentro da linha: não deixa o clique abrir a ficha da cotação. */
function BotaoComparar({ aoClicar, rotulo }: { aoClicar: () => void; rotulo: string }) {
  return (
    <Botao
      tamanho="sm"
      variante="secundario"
      icone={<GitCompareArrows className="size-4" />}
      onClick={(e) => {
        e.stopPropagation();
        aoClicar();
      }}
    >
      {rotulo}
    </Botao>
  );
}
